// Running the platform: dashboards, digests and audit, onboarding, the
// OSS-verified fee waiver, the operator-only admin plane, and invitations.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. A GROUP, never a meaning: a capability belongs to whichever group
// its `REST_ROUTES` neighbours are in, so these are absent from the barrel and a
// caller reads `CAPABILITY_MANIFEST`. `./manifest` is the only place they compose,
// and `capability-manifest.test.ts` proves the five are a PARTITION of it.

import { OPERATOR_MCP_ONLY } from './reasons';
import { type Capability, gap, mcp, na, yes } from './surface';

export const OPS_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // ── dashboards + digests + audit ──────────────────────────────────────────
  'dashboard.list': { rest: yes, mcp: mcp('dashboard.list'), ui: yes },
  'dashboard.get': { rest: yes, mcp: mcp('dashboard.get'), ui: yes },
  'dashboard.create': { rest: yes, mcp: mcp('dashboard.upsert'), ui: yes },
  'dashboard.update': { rest: yes, mcp: mcp('dashboard.patch'), ui: yes },
  'dashboard.delete': { rest: yes, mcp: mcp('dashboard.delete'), ui: yes },
  'dashboard.widget_page': { rest: yes, mcp: mcp('dashboard.widget_page'), ui: yes },
  'digest.list': { rest: yes, mcp: mcp('digest.list'), ui: yes },
  'digest.get': { rest: yes, mcp: mcp('digest.get'), ui: yes },
  'audit.tail': { rest: yes, mcp: mcp('audit.tail'), ui: yes },
  // The org console (#4391): a projection over `audit_events` the way
  // `audit.tail` is, so it sits beside it rather than under `session.*`.
  'terminal.tail': {
    rest: yes,
    mcp: na(
      'a rendered, keyed-line projection over `audit.tail`’s own rows — no second MCP address for one feed',
    ),
    // Slice 04 (#4395) ships the page this backs; #4394 is server-only.
    ui: gap('the Terminal page ships in #4395 — the route is served, nothing renders it yet'),
  },
  'terminal.stream': {
    rest: yes,
    mcp: na('an SSE tail is not a JSON-RPC shape (the `session.events` precedent)'),
    ui: gap('the Terminal page ships in #4395 — the route is served, nothing renders it yet'),
  },
  'terminal.status': {
    rest: yes,
    mcp: na(
      'composes `box.utilization` + `overview.counts`, both already served; no second address for their sum',
    ),
    ui: gap('the Terminal page ships in #4395 — the route is served, nothing renders it yet'),
  },

  // ── onboarding ────────────────────────────────────────────────────────────
  // The MCP nouns sit on the `config` resource — the account's setup surface —
  // and ride `read|write:account` rather than that resource's `read|write:runners`,
  // because the funnel is ACCOUNT state (`accounts.onboarding_state`), not agent
  // config. Authorization follows the DATA, never the resource noun.
  'onboarding.state': { rest: yes, mcp: mcp('config.onboarding_state'), ui: yes },
  'onboarding.advance': { rest: yes, mcp: mcp('config.onboarding_advance'), ui: yes },

  // ── OSS-verified fee waiver ───────────────────────────────────────────────
  // The APPLICANT's half, served on all three since plan 101 slice 04. It was the
  // sharpest asymmetry on this surface: the OPERATOR could read the queue and rule
  // on it from chat (`platform.oss_queue` / `oss_verify_decide`, below), and the
  // free-tier maintainer the waiver exists for could only be TOLD it existed. Both
  // MCP doors ride the REST routes' own `read|write:billing`, and the ask is filed
  // through the routes' own `submitOssApplication` — one composition, two doors, so
  // neither can come to its own opinion about what an application records.
  'account.oss_verification_get': {
    rest: yes,
    mcp: mcp('account.oss_verify_get'),
    ui: yes,
  },
  'account.oss_verification_apply': {
    rest: yes,
    mcp: mcp('account.oss_verify_apply'),
    ui: yes,
  },
  'account.oss_verify_list': { rest: yes, mcp: mcp('platform.oss_queue'), ui: yes },
  // The verdict's REST twin (plan 101 17-1): the console's door, on the same
  // `admin:accounts` operator credential, over the port the verb holds.
  'account.oss_verify_decide': { rest: yes, mcp: mcp('platform.oss_verify_decide'), ui: yes },

  // ── platform admin ────────────────────────────────────────────────────────
  // The MCP addresses are the operator-only `platform` row's: the radar band and
  // the `admin:accounts` overrides moved off `account` so no tenant catalog lists
  // them. The capability ids stay the REST routes' ids.
  //
  // THE MCP `na`s BELOW WERE WRITTEN FOR ONE AUDIENCE. They read "an agent working
  // a tenant's repo has no business" with this — true, and still true: no tenant
  // credential can reach the `platform` row. The platform's OWN operators driving it
  // from a chat client are the other audience, on admin-only scopes, so a closed
  // `na` became a served read or an owed `gap`.
  'account.platform_overview': { rest: yes, mcp: mcp('platform.overview'), ui: yes },
  'account.orgs_list': { rest: yes, mcp: mcp('platform.orgs_list'), ui: yes },
  'account.users_list': { rest: yes, mcp: mcp('platform.users_list'), ui: yes },
  'account.org_get': { rest: yes, mcp: mcp('platform.account_detail'), ui: yes },
  // One person across every org they belong to (#5727), trailed as `admin.tenant_read`.
  // `account.org_get`'s twin on the user side; the MCP read was not asked for.
  'account.user_get': {
    rest: yes,
    mcp: gap('owed a `platform` read beside `platform.users_list`; not asked for in #5727'),
    ui: yes,
  },
  // `audit.tail` is the ACCOUNT-scoped trail; this is the platform-wide one, and
  // they stay two verbs on two scopes rather than one that silently widens on a
  // staff scope — the shared-fence bug class #1418 records.
  'account.platform_audit': { rest: yes, mcp: mcp('platform.audit'), ui: yes },
  // The OTHER trail: what platform STAFF did TO tenants (`platform_audit_events`),
  // as opposed to the row above, which is what the platform did FOR them.
  'account.platform_trail': { rest: yes, mcp: mcp('platform.trail'), ui: yes },
  // The CROSS-TENANT run reads (`admin:read:runs`). A run transcript is the agent's
  // reasoning, the customer's code, their commands and their test output, and the
  // only reader with standing to see another tenant's is a named human whose read
  // leaves a `platform_audit_events` row. The `platform` row now trails every
  // tenant-disclosing read by that human actor first (and refuses a user-less
  // token), so the old objection — "an agent verb names no human to trail" — no
  // longer holds. Owed, not closed.
  'account.platform_run_get': {
    rest: yes,
    mcp: gap('owed on the `platform` row, which trails reads by human actor; not built yet'),
    ui: yes,
  },
  'account.platform_run_tail': {
    rest: yes,
    mcp: gap('owed on the `platform` row, which trails reads by human actor; not built yet'),
    // The transcript view is the other agent's slice; the run page links into it.
    ui: yes,
  },
  // The hive band (plan 102 slice 10, #3097): every fan-out lane's self-report,
  // graded through the `hive-event.ts` contract — staff radar over our own fleet.
  'account.platform_hive': { rest: yes, mcp: mcp('platform.hive'), ui: yes },
  // The autonomous goal monitor: the PLATFORM's own milestones and epics off GitHub.
  'account.platform_goals': { rest: yes, mcp: mcp('platform.goals'), ui: yes },
  // The nightly checkup's platform-wide telemetry (plan 101 slice 06, #5434):
  // aggregates only, trailed. Built MCP-first because its first reader is the
  // checkup; a staff human reading the same picture is owed both other doors.
  'account.platform_health': {
    rest: gap('owed a `GET /v1/admin/health` twin over the same `platformHealth` read'),
    mcp: mcp('platform.health'),
    ui: gap('owed a health band on the admin radar; only the MCP door serves it yet'),
  },
  // The board's ONE write: retitle a milestone or epic on GitHub itself. A PR-4
  // `na` ("an operator act, not an agent capability") that assumed a tenant's agent
  // was the caller; for the platform's own operators it is an owed `platform`
  // mutation (with `confirm: true`), deferred behind the read-only overviews.
  'account.platform_goal_rename': {
    rest: yes,
    mcp: gap('owed on the `platform` row as a confirm-gated mutation; not built yet'),
    ui: yes,
  },
  'account.set_tier': { rest: yes, mcp: mcp('platform.set_tier'), ui: yes },
  'account.set_seats': { rest: yes, mcp: mcp('platform.set_seats'), ui: yes },
  'account.set_spend_cap': { rest: yes, mcp: mcp('platform.set_spend_cap'), ui: yes },
  // #5716: re-run the GitHub backlog import for an already-enabled repo — the lane's
  // only other producer is the first enable. An operator one-shot over REST for now.
  'account.backlog_import': {
    rest: yes,
    mcp: gap('owed on the `platform` row as a confirm-gated mutation; not built yet'),
    ui: gap('no admin console lever yet; an operator credential calls the REST route'),
  },
  // #6299: enqueue the daily `cron.builtin_dashboard_sync` lane on demand so a
  // rollback window doesn't drag the Fleet Workers widget to a 500 for up to 24h.
  // The lane's daily 04:10 UTC tick is the only other producer; this is the second.
  'builtin_dashboard_sync.enqueue': {
    rest: yes,
    mcp: gap('owed on the `platform` row as a confirm-gated mutation; not built yet'),
    ui: gap('no admin console lever yet; an operator credential calls the REST route'),
  },
  // Staff change a member's role in an org they hold no membership in (#5727); the
  // last-owner rule still binds on both doors, which share one port.
  'account.set_member_role': {
    rest: yes,
    mcp: mcp('platform.set_member_role'),
    ui: gap('role control waits on the operator-credential step, #5724'),
  },
  'user.approve': { rest: OPERATOR_MCP_ONLY, mcp: mcp('user.approve'), ui: yes },
  'user.suspend': { rest: OPERATOR_MCP_ONLY, mcp: mcp('user.suspend'), ui: yes },
  // The BY-ID pair. `users.login` carries no unique index, so a handle can name
  // several rows and both by-login levers REFUSE that case rather than picking a
  // person; these are how the operator then acts on the row they mean.
  'user.approve_by_id': {
    rest: yes,
    mcp: mcp('user.approve_by_id'),
    ui: gap(
      'the admin console approves by login and has no by-id lever for a duplicated handle yet',
    ),
  },
  'user.suspend_by_id': {
    rest: yes,
    mcp: mcp('user.suspend_by_id'),
    ui: gap(
      'the admin console suspends by login and has no by-id lever for a duplicated handle yet',
    ),
  },
  // ── invitations (arc 101 slice 04) ────────────────────────────────────────
  // The platform-access half. Same operator posture as the two rows above: the
  // grant they hand out is wider (an address that has never signed up), so the
  // REST surface is deliberately the admin verbs' and the dashboard is owed a
  // screen it does not have yet.
  'user.invite': {
    rest: OPERATOR_MCP_ONLY,
    mcp: mcp('user.invite'),
    ui: gap('the admin console lists waitlisted users but has no invite-by-email form yet'),
  },
  // ── the sign-up funnel (arc 101 slice 01) ─────────────────────────────────
  // The operator's one door is the MCP `admin` verb; every action has a REST
  // twin under /v1/admin/* (#3703 pattern) and the admin console drives the
  // twin. The listing and the revoke moved off `user` (`user.invites` /
  // `user.revoke_invite`) so the whole funnel reads from one verb.
  // The dashboard's crash sink (plan 101 07-4): a signed-in browser's own error
  // hooks are the only caller. `na` on both, not `gap`: no agent has a browser
  // runtime to crash, and the report is telemetry the SPA sends, not a screen.
  'client_error.report': {
    rest: yes,
    mcp: na('a browser crash report; no agent has a browser runtime to crash'),
    ui: na('the dashboard SENDS it from its error hooks; telemetry, not a screen'),
  },
  // The applicant's door: the marketing form POSTs over its service token. No
  // MCP address and no dashboard screen, and both are `na`, not `gap` — an
  // applicant has no credential and no account, so neither surface can exist.
  'access_request.create': {
    rest: yes,
    mcp: na('the applicant has no credential; the waitlist form is the only writer'),
    ui: na('the applicant has no account; the form lives on the marketing site'),
  },
  'access_request.list': { rest: yes, mcp: mcp('admin.access_requests_list'), ui: yes },
  'access_request.get': {
    rest: yes,
    mcp: mcp('admin.access_requests_get'),
    ui: gap('the applications screen renders the list; there is no per-application page'),
  },
  'access_request.invite': { rest: yes, mcp: mcp('admin.access_requests_invite'), ui: yes },
  'access_request.decline': { rest: yes, mcp: mcp('admin.access_requests_decline'), ui: yes },
  'invitation.list': { rest: yes, mcp: mcp('admin.invites_list'), ui: yes },
  'invitation.resend': { rest: yes, mcp: mcp('admin.invites_resend'), ui: yes },
  'invitation.revoke': { rest: yes, mcp: mcp('admin.invites_revoke'), ui: yes },
  // The redeem leg. REST-only on purpose, and the two absences are different
  // kinds of absence.
  'invitation.accept': {
    rest: yes,
    mcp: na(
      'accepting BINDS a verified human identity to a grant. An agent credential names a ' +
        'human but is not one, and the whole point of the leg is that the person who received ' +
        'the mail is the one who signed in',
    ),
    ui: gap(
      'the SPA owes the /invite landing that reads the token out of the link and POSTs it after sign-in',
    ),
  },
  // `/admin/grants` — the subject is PICKED from the org/user registries rather
  // than typed, because the listing does not resolve it: an id nobody holds
  // answers an empty history, indistinguishable from a subject with no grants.
  'grant.list': { rest: yes, mcp: mcp('grant.list'), ui: yes },
  'grant.issue': { rest: yes, mcp: mcp('grant.issue'), ui: yes },
  'grant.revoke': { rest: yes, mcp: mcp('grant.revoke'), ui: yes },
  // `POST /v1/admin/provision` — the one door that takes an EMAIL and lands a
  // usable account: approve the human if they have already signed in, invite
  // them if they have not, then issue the grants. REST is a real surface here,
  // unlike the operator rows above, because the provisioning script and the MCP
  // verb both drive the same route.
  //
  // `gap`, not `na`, for the UI: the admin console is owed this form for the
  // same reason it is owed the invite one — it can list waitlisted users but
  // cannot act on an address that has never signed up.
  'grant.provision': {
    rest: yes,
    mcp: mcp('user.provision'),
    ui: gap('the admin console has no provision-by-email form yet — same gap as `user.invite`'),
  },
  // ── staff bug reports (plan 101 slice 05, #5427) ──────────────────────────
  // `platform_bugs`: the platform's OWN defect intake, filed by developerz-ai staff
  // on `admin:bugs` (never on a tenant ceiling) and screened on write before the
  // nightly checkup can read a body. MCP is the first door because staff file from
  // their chat client; REST and the admin console are owed, not closed — nothing
  // about a bug report is chat-shaped, so the question stays open.
  'bug.report': {
    rest: gap('no /v1/admin route files a platform bug yet — staff report over MCP'),
    mcp: mcp('bug.report'),
    ui: gap('the admin console has no bug-report form yet'),
  },
  'bug.list': {
    rest: gap('no /v1/admin route lists platform bugs yet — the checkup reads over MCP'),
    mcp: mcp('bug.list'),
    ui: gap('the admin console has no platform-bug queue yet'),
  },
  'bug.verdict': {
    rest: gap('no /v1/admin route records a bug verdict yet — the checkup rules over MCP'),
    mcp: mcp('bug.verdict'),
    ui: gap('the admin console has no verdict form on the platform-bug queue yet'),
  },
};
